<template>
  <b-popover
    v-bind="$attrs"
    :show.sync="showPopover"
    :custom-class="popoverClass"
    triggers="focus"
  >
    <slot v-bind="{ column }">
      <b-calendar
        v-if="!!column.date"
        v-model="search"
        value-as-date
        @input="applySearch"
      ></b-calendar>

      <form v-else class="p-2" @submit.prevent.stop="applySearch">
        <DatePicker
          v-if="column.datePicker"
          v-model="search"
          :range="column.datePicker.range || false"
          :type="column.datePicker.type || 'date'"
          inline
        />
        <div v-else-if="!!column.multiSelect" class="base-data-table-multiselect">
          <BaseInput
            v-model="optionSearch"
            :placeholder="`Search ${column.label}`"
            size="sm"
          />
          <div class="base-data-table-multiselect__list mt-2">
            <b-form-checkbox-group v-model="selected" stacked>
              <b-form-checkbox
                v-for="option in visibleOptions"
                :key="option"
                :value="option"
                class="small"
              >
                {{ option }}
              </b-form-checkbox>
            </b-form-checkbox-group>
            <p v-if="!visibleOptions.length" class="small text-muted mb-0">No matches.</p>
          </div>
          <div class="d-flex justify-content-between small mt-1">
            <span class="text-muted">{{ selected.length }} selected</span>
            <a v-if="selected.length" role="button" @click.prevent="selected = []">Clear</a>
          </div>
        </div>
        <BaseSelect
          v-else-if="!!column.options"
          v-model="search"
          :options="column.options"
          size="sm"
        >
          <template v-slot:first>
            <option :value="null" disabled>Select {{ column.label }}</option>
          </template>
        </BaseSelect>
        <BaseInput
          v-else
          v-model="search"
          :placeholder="`Search by ${column.label}`"
          size="sm"
        />

        <b-button variant="primary" size="sm" type="submit" class="mt-2"
          >Apply</b-button
        >
      </form>
    </slot>
  </b-popover>
</template>

<script>
import { defineComponent, computed, inject, ref, watch } from '@vue/composition-api'
import DatePicker from 'vue2-datepicker'

import { BaseInput, BaseSelect } from '@components/base'

import { BaseDataTableInjectionKey } from './BaseDataTable.constants'

export default defineComponent({
  name: 'BaseDataTableSearchPopover',
  components: {
    DatePicker,

    BaseInput,
    BaseSelect
  },
  props: {
    columnName: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const { columns, filters, addFilter } = inject(BaseDataTableInjectionKey)

    const column = computed(() =>
      columns.value.find((column) => column.key === props.columnName)
    )

    const showPopover = ref(false)

    const popoverClass = computed(() => {
      return Object.entries({
        'popover-daterange': !!column.value.datePicker,
        'popover-datepicker--range':
          !!column.value.datePicker && column.value.datePicker.range
      })
        .filter(([_, value]) => !!value)
        .map(([name]) => name)
        .join(' ')
    })

    const search = ref('')

    // Multi-select columns (LIQ-2656): a typeahead over the column's options,
    // any number of which can be picked. Opening the popover again starts
    // from the values already applied.
    const optionSearch = ref('')
    const selected = ref([])
    const allOptions = computed(() => {
      const source = column.value.multiSelect && column.value.multiSelect.options
      return (typeof source === 'function' ? source() : source) || []
    })
    const visibleOptions = computed(() => {
      const term = optionSearch.value.trim().toLowerCase()
      return term
        ? allOptions.value.filter((option) => String(option).toLowerCase().includes(term))
        : allOptions.value
    })
    watch(showPopover, (open) => {
      if (!open || !column.value.multiSelect) return
      const applied = filters.value.find((filter) => filter.key === column.value.key && filter.multi)
      selected.value = applied ? [...applied.value] : []
      optionSearch.value = ''
    })

    const applySearch = () => {
      if (column.value.multiSelect) {
        addFilter(column.value.key, selected.value, false, true)
        showPopover.value = false
        return
      }

      if (column.value.datePicker && !!column.value.datePicker.range) {
        addFilter(column.value.key, search.value, true)
      } else {
        addFilter(column.value.key, search.value)
      }

      search.value = ''

      showPopover.value = false
    }

    return {
      showPopover,
      popoverClass,

      column,

      search,
      applySearch,

      optionSearch,
      selected,
      visibleOptions
    }
  }
})
</script>

<style scoped>
.base-data-table-multiselect {
  min-width: 220px;
}

.base-data-table-multiselect__list {
  max-height: 220px;
  overflow-y: auto;
}

.mx-datepicker {
  min-width: 250px;
  display: block;
}

.mx-datepicker-range {
  min-width: 500px;
}

.mx-datepicker >>> .mx-input {
  border-radius: 4px;
  border-color: #e9ecef;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.075);
}
</style>

<style>
.popover.popover-datepicker--range {
  max-width: 515px;
}
</style>
